import { Link } from 'react-router-dom';

import { SEO } from '@platform/seo';

import { buildSeo } from '@/shared/seo/seoBuilder';

import { PumsemChangeBadge } from '../components/PumsemChangeBadge';
import { pumsemItemPath } from '../lib/pumsemNavigation';
import { countPumsemItems } from '../lib/pumsemTree';
import type { PumsemNodeHit } from '../model/pumsemTree.type';

interface PumsemGroupViewProps {
  hit: PumsemNodeHit;
  edition: string | null;
  editionLabel: string;
}

/**
 * 장·절 목차 — `/pumsem/machine/6-2` 처럼 **항목이 아닌 코드**로 들어온 화면.
 *
 * 예전에는 상세 API 가 404 를 내고 "해당 품셈을 찾을 수 없습니다" 로 끝났다.
 * 그런데 코드 마디를 뒤에서부터 지워 가며 범위를 넓히는 건(`6-2-4` → `6-2`)
 * 실무에서 아주 흔한 동작이고, 견적서·문서에 인용된 코드도 절 단위인 경우가
 * 많다. 그때 "없는 품셈" 이라고 답하면 사용자는 자기 코드가 틀렸다고 읽는다.
 *
 * 데이터는 **이미 화면이 들고 있는 트리**에서 온다(왼쪽 목차와 같은 캐시).
 * 장·절 전용 API 를 새로 파지 않은 이유: 트리 응답이 이미 코드·제목·개정
 * 표기까지 다 갖고 있고, 목차에 그 이상 필요한 게 없다.
 */
export function PumsemGroupView({
  hit,
  edition,
  editionLabel,
}: PumsemGroupViewProps) {
  const { division, node, ancestors } = hit;
  const itemCount = countPumsemItems(node);

  const seo = buildSeo({
    title: `${node.code} ${node.title}`,
    description: `${division.title} ${node.code} ${node.title} — ${editionLabel} 표준품셈 목차. 항목 ${itemCount}개.`,
    path: pumsemItemPath(division.slug, node.code, edition),
  });

  return (
    <>
      <SEO {...seo} />

      <div className="min-w-0 p-5 sm:p-8">
        <nav aria-label="위치" className="mb-3 text-[13.5px] text-neutral-400">
          <span className="text-neutral-500">{division.title}</span>
          {ancestors.map((parent) => (
            <span key={parent.code}>
              <span className="px-1.5 text-neutral-300">›</span>
              <Link
                to={pumsemItemPath(division.slug, parent.code, edition)}
                className="text-neutral-500 hover:text-brand-700 hover:underline dark:hover:text-brand-300"
              >
                {parent.code} {parent.title}
              </Link>
            </span>
          ))}
        </nav>

        <h1 className="text-[28px] leading-tight font-black tracking-tight text-neutral-900 dark:text-neutral-50">
          <span className="tabular-nums">{node.code}</span>
          <span className="px-2 text-neutral-300">·</span>
          {node.title}
        </h1>

        <p className="mt-1.5 mb-6 text-[14px] text-neutral-500">
          {editionLabel} · 하위 {node.children.length}개 · 항목 {itemCount}개
        </p>

        {node.children.length === 0 ? (
          <p className="rounded-xl border border-dashed border-neutral-200 px-4 py-10 text-center text-[14.5px] text-neutral-400 dark:border-white/12">
            이 판에는 아래 내용이 아직 없습니다.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-neutral-200 dark:border-white/12">
            {node.children.map((child) => (
              <li
                key={child.code}
                className="border-t border-neutral-100 first:border-t-0 dark:border-white/8"
              >
                <Link
                  to={pumsemItemPath(division.slug, child.code, edition)}
                  className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50 dark:hover:bg-white/5"
                >
                  <code className="w-20 shrink-0 text-[13px] tabular-nums text-neutral-400">
                    {child.code}
                  </code>
                  <span
                    className={`min-w-0 text-[14.5px] text-neutral-800 dark:text-neutral-100 ${
                      child.isItem ? '' : 'font-bold'
                    }`}
                  >
                    {child.title}
                  </span>
                  {child.changeKind && (
                    <PumsemChangeBadge change={child.changeKind} />
                  )}
                  {/* 항목인지 또 하나의 목차인지를 눌러 보기 전에 알려준다. */}
                  {!child.isItem && (
                    <span className="ml-auto shrink-0 text-[13px] text-neutral-400">
                      {countPumsemItems(child)}개 항목
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
